import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = 'kiteiq_token';
const REFRESH_KEY = 'kiteiq_refresh';

// --- Centralized token storage helpers --------------------------------------
export const getAccessToken = () => localStorage.getItem(TOKEN_KEY);
export const getRefreshToken = () => localStorage.getItem(REFRESH_KEY);

export const setAuthTokens = (token, refresh) => {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  if (refresh) localStorage.setItem(REFRESH_KEY, refresh);
};

export const clearAuthTokens = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_KEY);
};

const api = axios.create({ baseURL: API });

api.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// --- Silent access-token refresh on 401 -------------------------------------
// When a short-lived access token expires, transparently swap it for a fresh
// one using the long-lived refresh token, then retry the original request once.
// Concurrent 401s share a single in-flight refresh to avoid a refresh stampede.
let refreshPromise = null;
const AUTH_SKIP = ['/auth/refresh', '/auth/login', '/auth/register'];

api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const original = error.config;
    const status = error?.response?.status;
    const url = original?.url || '';
    const isAuthCall = AUTH_SKIP.some((p) => url.includes(p));
    const refresh = getRefreshToken();

    if (status === 401 && original && !original._retry && !isAuthCall && refresh) {
      original._retry = true;
      try {
        if (!refreshPromise) {
          refreshPromise = axios
            .post(`${API}/auth/refresh`, { refresh_token: refresh })
            .then((r) => r.data)
            .finally(() => { refreshPromise = null; });
        }
        const data = await refreshPromise;
        setAuthTokens(data.token, data.refresh_token);
        original.headers = original.headers || {};
        original.headers.Authorization = `Bearer ${data.token}`;
        return api(original);
      } catch (e) {
        // Refresh failed → the session is truly over. Clear tokens and let the
        // app react (AuthContext listens for this event to reset the user).
        clearAuthTokens();
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('kiteiq:session-expired'));
        }
        return Promise.reject(error);
      }
    }
    return Promise.reject(error);
  }
);

export const videoUrl = (analysisId) => `${API}/videos/${analysisId}`;

// Build an absolute URL for a backend-served media path (e.g. /api/static/...).
export const mediaUrl = (path) => {
  if (!path) return path;
  if (/^https?:\/\//.test(path)) return path;
  return `${BACKEND_URL}${path}`;
};

// Safely extract a human-readable error string from an axios error.
// Handles FastAPI/Pydantic 422 responses where `detail` is an array of
// objects ({type, loc, msg, ...}), preventing React render crashes.
export const apiError = (err, fallback = 'Something went wrong') => {
  const detail = err?.response?.data?.detail;
  if (!detail) return err?.message || fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    const msgs = detail.map((d) => (typeof d === 'string' ? d : d?.msg)).filter(Boolean);
    return msgs.length ? msgs.join(', ') : fallback;
  }
  if (typeof detail === 'object') return detail.msg || fallback;
  return fallback;
};

export default api;
